/**
 * Chrome and Safari aggressively throttle background tabs:
 *   - `requestAnimationFrame` → 1 Hz
 *   - `requestVideoFrameCallback` → ~0 Hz
 *   - setInterval / setTimeout → ≥1 s
 *
 * This destroys our recording fidelity whenever the user switches tabs
 * mid-capture (e.g. to press play on a YouTube video they're
 * screen-sharing). Content-starved compose/encode loops then only output
 * ~1 fps for the duration the user is off-tab, which looks like "the
 * opening was cut".
 *
 * Workaround: play a silent audio node. Tabs with active audio output
 * are excluded from the throttling heuristics (the browser assumes the
 * user is still listening to them). A muted oscillator is the cheapest
 * way to do this — zero CPU overhead, no microphone/mic permission.
 *
 * Must be created within a user gesture (e.g. the Record button click
 * handler) to satisfy autoplay policy. Call `release()` on recording
 * stop so the AudioContext isn't leaked.
 */
export class KeepTabActive {
  private ctx: AudioContext | null = null;
  private osc: OscillatorNode | null = null;

  acquire(): boolean {
    if (this.ctx) return true; // already active
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return false;
      const ctx = new Ctor({ latencyHint: 'playback' });
      // Some browsers start AudioContext suspended; resume synchronously
      // inside the user gesture.
      void ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0; // fully silent
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      this.ctx = ctx;
      this.osc = osc;
      return true;
    } catch (err) {
      console.warn('[KeepTabActive] acquire failed', err);
      return false;
    }
  }

  release(): void {
    try {
      this.osc?.stop();
      this.osc?.disconnect();
    } catch {
      /* ignore */
    }
    this.osc = null;
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      void ctx.close().catch(() => {});
    }
  }

  isActive(): boolean {
    return this.ctx !== null;
  }
}
